import React, { FC } from 'react';

import BlockContent from '@sanity/block-content-to-react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import {
  submitGeneralInquiry,
  submitBusinessOnboardingForm,
} from 'state/actions/formActions';

import { Form, BasePortableText } from 'styled';

import Language from 'constants/Language';
import {
  ExternalLink,
  InternalLink,
  ListItem,
} from 'constants/PortableTextSerializer';

import { GlobalSettingsReducer } from 'state/reducers/globalSettings';
import { GlobalState, Status, FormValues } from 'types';

interface StoreProps {
  globalSettings: GlobalSettingsReducer;
  submitGeneralInquiryStatus: Status;
  submitBusinessOnboardingFormStatus: Status;
}

interface DispatchProps {
  actions: {
    submitGeneralInquiry(formValues: FormValues): void;
    submitBusinessOnboardingForm(form: FormValues): void;
  };
}

type Props = StoreProps & DispatchProps;

const GeneralInquiryModule: FC<Props> = ({
  globalSettings,
  submitGeneralInquiryStatus,
  submitBusinessOnboardingFormStatus,
  actions,
}) => {
  const { generalInquiry } = globalSettings;
  const {
    sectionTitle,
    sectionDescription,
    formDescription,
    inputFields,
  } = generalInquiry;
  const { submitGeneralInquiry, submitBusinessOnboardingForm } = actions;

  const handleFormSubmit = (formValues: FormValues) => {
    if (generalInquiry.submitFormToEmail) {
      submitGeneralInquiry(formValues);
    } else {
      submitBusinessOnboardingForm(formValues);
    }
  };

  return (
    <div className="GeneralInquiryModule relative z-overlay w100 module-content-padding-x py6 xl:flex justify-center">
      <div className="xl:inline-flex flex-col">
        <div className="text-lg mb_75">{sectionTitle}</div>
        <div className="flex flex-col md:flex-row mt3_5 md:mt6">
          <BasePortableText
            textColor="black"
            className="GeneralInquiryModule__section-description text-sm"
          >
            <BlockContent
              blocks={sectionDescription}
              serializers={{
                marks: { link: ExternalLink, internalLink: InternalLink },
                listItem: ListItem,
              }}
            />
          </BasePortableText>
          <div className="md:ml5_5 xl:ml6 flex-1">
            {!!formDescription && (
              <div className="Form__description my3 md:my0">
                <span className="text-sm">{formDescription}</span>
              </div>
            )}
            <Form
              className="GeneralInquiryModule__form w100"
              color="gray-dark"
              inputFields={inputFields}
              handleSubmit={(formValues: FormValues) => {
                handleFormSubmit(formValues);
              }}
              formSubmitStatus={
                generalInquiry.submitFormToEmail
                  ? submitGeneralInquiryStatus
                  : submitBusinessOnboardingFormStatus
              }
              submitButtonLabel={Language.t(
                'GeneralInquiryModule.sendButtonLabel'
              )}
              title="GeneralInquiryModule"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: GlobalState): StoreProps => ({
  globalSettings: state.globalSettings,
  submitGeneralInquiryStatus: state.status.submitGeneralInquiryStatus,
  submitBusinessOnboardingFormStatus:
    state.status.submitBusinessOnboardingFormStatus,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  actions: bindActionCreators(
    {
      submitGeneralInquiry,
      submitBusinessOnboardingForm,
    },
    dispatch
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GeneralInquiryModule);
